import { httpsCallable } from 'firebase/functions';
import { functionsSa } from '../firebase';
import type { ApiResponse } from './firebaseFunctions';

export type EntitlementUploadJobStatus =
  | 'awaiting_upload'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'expired';

export interface EntitlementUploadJobProgress {
  jobId: string;
  status: EntitlementUploadJobStatus;
  totalRows: number | null;
  processedRows: number;
  deliveredRows: number;
  pendingRows: number;
  errorRows: number;
  createdEntitlements: number;
  createdPendingEntitlements: number;
  createdAt: string;
  completedAt?: string;
  retentionExpiresAt: string;
  failure?: { code: string; message: string };
  report?: { jobId: string; storagePath: string; fileName: string; sizeBytes: number };
}

interface UploadEntitlementUploadResponse {
  jobId: string;
  status: Extract<EntitlementUploadJobStatus, 'queued' | 'processing' | 'completed' | 'failed'>;
}

interface StartEntitlementUploadJobResponse {
  jobId: string;
  status: Extract<EntitlementUploadJobStatus, 'queued' | 'processing' | 'completed' | 'failed'>;
}

const callableData = async <T>(name: string, payload: unknown): Promise<T> => {
  const response = (await httpsCallable(functionsSa, name)(payload)).data as ApiResponse<T>;
  if (!response.success || response.data === undefined) {
    throw new Error(response.error || 'Não foi possível concluir o upload.');
  }
  return response.data;
};

const contentTypeFor = (file: File): 'text/csv' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' => (
  file.name.toLowerCase().endsWith('.xlsx')
    ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    : 'text/csv'
);

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    const end = Math.min(offset + 0x8000, bytes.length);
    for (let index = offset; index < end; index += 1) binary += String.fromCharCode(bytes[index]);
  }
  return btoa(binary);
}

export const entitlementUploadService = {
  async createJob(file: File, onProgress?: (progress: number) => void): Promise<StartEntitlementUploadJobResponse> {
    const contentType = contentTypeFor(file);
    onProgress?.(10);
    const fileBase64 = arrayBufferToBase64(await file.arrayBuffer());
    onProgress?.(45);
    return callableData<UploadEntitlementUploadResponse>('uploadEntitlementUpload', {
      fileName: file.name,
      contentType,
      sizeBytes: file.size,
      fileBase64,
    });
  },

  getStatus: (jobId: string) => callableData<EntitlementUploadJobProgress>(
    'getEntitlementUploadJobStatus',
    { jobId },
  ),

  async downloadReport(report: NonNullable<EntitlementUploadJobProgress['report']>): Promise<void> {
    const download = await callableData<{ fileName: string; contentType: string; fileBase64: string }>(
      'downloadEntitlementUploadReport',
      { jobId: report.jobId },
    );
    const binary = atob(download.fileBase64);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    const blob = new Blob([bytes], { type: download.contentType });
    const objectUrl = URL.createObjectURL(blob);
    let link: HTMLAnchorElement | undefined;
    try {
      link = document.createElement('a');
      link.href = objectUrl;
      link.download = report.fileName;
      document.body.appendChild(link);
      link.click();
    } finally {
      link?.remove();
      URL.revokeObjectURL(objectUrl);
    }
  },
};
